/**
 * Design da pagina publica do formulario (config.design).
 *
 * Um unico lugar decide (a) os defaults, (b) o que e' valor valido e (c) como
 * o design vira CSS. A aba Design, o preview e a pagina /f/:slug consomem a
 * mesma funcao, entao o que o gestor ve no preview e' o que o visitante ve.
 * Espelha `formularios.services.normalizar_design` (backend), que e' quem
 * decide o que persiste — chave nova entra nos dois lados ou em nenhum.
 */

export const DESIGN_PADRAO = Object.freeze({
  // Pagina
  pagina_bg: "",
  pagina_bg_imagem: "",
  pagina_bg_ajuste: "cover",
  pagina_padding: 32,
  fonte_familia: "sistema",
  fonte_base: 14,
  // Cartao
  cartao_largura: 640,
  cartao_bg: "",
  cartao_padding: 28,
  cartao_raio: 16,
  cartao_sombra: "suave",
  cartao_borda: true,
  barra_topo: true,
  // Cabecalho
  logo_url: "",
  logo_altura: 48,
  logo_alinhamento: "esquerda",
  imagem_topo_url: "",
  titulo_cor: "",
  titulo_tamanho: 24,
  texto_cor: "",
  // Campos
  gap_campos: 14,
  colunas: 2,
  input_raio: 8,
  input_bg: "",
  label_cor: "",
  // Botao
  botao_texto: "Enviar",
  botao_largura: "auto",
  botao_alinhamento: "esquerda",
  botao_raio: 8,
  botao_cor: "",
  botao_texto_cor: "",
  // Celular / rodape
  empilhar_no_celular: true,
  mobile_padding: 16,
  rodape_texto: "",
});

export const FONTES = [
  { value: "sistema", label: "Padrão do sistema", css: "" },
  { value: "serif", label: "Serifada (Georgia)", css: "Georgia, 'Times New Roman', serif" },
  { value: "display", label: "Display (a dos títulos)", css: "var(--font-display)" },
  { value: "mono", label: "Monoespaçada", css: "var(--font-mono)" },
];

export const OPCOES_DESIGN = {
  pagina_bg_ajuste: [
    ["cover", "Cobrir a página"],
    ["contain", "Caber inteira"],
    ["repeat", "Repetir (padrão)"],
  ],
  cartao_sombra: [
    ["nenhuma", "Sem sombra"],
    ["suave", "Suave"],
    ["forte", "Forte"],
  ],
  logo_alinhamento: [
    ["esquerda", "À esquerda"],
    ["centro", "Centralizado"],
  ],
  botao_largura: [
    ["auto", "Do tamanho do texto"],
    ["total", "Largura total"],
  ],
  botao_alinhamento: [
    ["esquerda", "À esquerda"],
    ["centro", "Centralizado"],
    ["direita", "À direita"],
  ],
};

const SOMBRAS = {
  nenhuma: "none",
  suave: "var(--shadow-2)",
  forte: "0 24px 60px rgba(0, 0, 0, 0.28)",
};

const JUSTIFY = {
  esquerda: "flex-start",
  centro: "center",
  direita: "flex-end",
};

// Intervalos aceitos — os mesmos de `_DESIGN_NUMEROS` no backend, para o
// preview mostrar o que vai ser salvo (o backend traz para dentro do
// intervalo em silencio).
export const LIMITES = Object.freeze({
  pagina_padding: [0, 200],
  cartao_largura: [320, 1400],
  cartao_padding: [0, 120],
  cartao_raio: [0, 60],
  gap_campos: [0, 60],
  colunas: [1, 2],
  input_raio: [0, 40],
  botao_raio: [0, 40],
  logo_altura: [16, 240],
  titulo_tamanho: [12, 72],
  fonte_base: [11, 24],
  mobile_padding: [0, 80],
});

const NUMERICAS = new Set(
  Object.keys(DESIGN_PADRAO).filter((k) => typeof DESIGN_PADRAO[k] === "number")
);
const BOOLEANAS = new Set(
  Object.keys(DESIGN_PADRAO).filter((k) => typeof DESIGN_PADRAO[k] === "boolean")
);

/** Design completo: defaults + o que veio do backend, no tipo certo. */
export function normalizarDesign(bruto) {
  const fonte = bruto && typeof bruto === "object" ? bruto : {};
  const saida = { ...DESIGN_PADRAO };
  Object.keys(DESIGN_PADRAO).forEach((chave) => {
    const valor = fonte[chave];
    if (valor === undefined || valor === null) return;
    if (NUMERICAS.has(chave)) {
      const n = Number(valor);
      if (Number.isFinite(n)) {
        const [minimo, maximo] = LIMITES[chave] || [-Infinity, Infinity];
        saida[chave] = Math.round(Math.min(Math.max(n, minimo), maximo));
      }
    } else if (BOOLEANAS.has(chave)) {
      saida[chave] = Boolean(valor);
    } else {
      saida[chave] = String(valor);
    }
  });
  return saida;
}

/** True quando nada foi personalizado (botao "Restaurar padrão" desnecessario). */
export function designEhPadrao(design) {
  const d = normalizarDesign(design);
  return Object.keys(DESIGN_PADRAO).every((k) => d[k] === DESIGN_PADRAO[k]);
}

/** Só o que difere do padrão — é o que vale a pena persistir. */
export function designParaSalvar(design) {
  const d = normalizarDesign(design);
  const saida = {};
  Object.keys(DESIGN_PADRAO).forEach((k) => {
    if (d[k] !== DESIGN_PADRAO[k]) saida[k] = d[k];
  });
  return saida;
}

const px = (n) => `${Math.round(Number(n) || 0)}px`;

/**
 * Design -> variaveis CSS. `--pub-*` vestem a pagina/cartao (styles.jsx:
 * PublicPage/PublicCard/...), `--fb-*` vestem o formulario (RenderScope).
 * Variavel de cor so e' definida quando o gestor escolheu uma — sem ela o
 * CSS cai no fallback do tema, e o formulario continua respeitando claro/
 * escuro.
 */
export function varsDoDesign(design) {
  const d = normalizarDesign(design);
  const vars = {};

  if (d.pagina_bg) vars["--pub-bg"] = d.pagina_bg;
  if (d.pagina_bg_imagem) {
    vars["--pub-bg-image"] = `url("${d.pagina_bg_imagem.replace(/["\\]/g, "")}")`;
    vars["--pub-bg-size"] = d.pagina_bg_ajuste === "repeat" ? "auto" : d.pagina_bg_ajuste;
    vars["--pub-bg-repeat"] = d.pagina_bg_ajuste === "repeat" ? "repeat" : "no-repeat";
  }
  vars["--pub-page-pad"] = px(d.pagina_padding);
  const fonte = FONTES.find((f) => f.value === d.fonte_familia);
  if (fonte?.css) vars["--pub-font"] = fonte.css;
  vars["--pub-font-size"] = px(d.fonte_base);

  vars["--pub-card-w"] = px(d.cartao_largura);
  if (d.cartao_bg) vars["--pub-card-bg"] = d.cartao_bg;
  vars["--pub-card-pad"] = px(d.cartao_padding);
  vars["--pub-card-radius"] = px(d.cartao_raio);
  vars["--pub-card-shadow"] = SOMBRAS[d.cartao_sombra] || SOMBRAS.suave;
  vars["--pub-card-border"] = d.cartao_borda ? "1px solid var(--border)" : "0";
  vars["--pub-topbar"] = d.barra_topo ? "block" : "none";

  vars["--pub-logo-h"] = px(d.logo_altura);
  vars["--pub-logo-align"] = d.logo_alinhamento === "centro" ? "center" : "left";
  if (d.titulo_cor) vars["--pub-title-color"] = d.titulo_cor;
  vars["--pub-title-size"] = px(d.titulo_tamanho);
  if (d.texto_cor) vars["--pub-text-color"] = d.texto_cor;
  vars["--pub-mobile-pad"] = px(d.mobile_padding);

  vars["--fb-gap"] = px(d.gap_campos);
  vars["--fb-cols"] = String(d.colunas === 1 ? 1 : 2);
  vars["--fb-cols-mobile"] = d.empilhar_no_celular ? "1" : vars["--fb-cols"];
  vars["--fb-input-radius"] = px(d.input_raio);
  if (d.input_bg) vars["--fb-input-bg"] = d.input_bg;
  if (d.label_cor) vars["--fb-label-color"] = d.label_cor;
  vars["--fb-btn-radius"] = px(d.botao_raio);
  if (d.botao_cor) vars["--fb-btn-bg"] = d.botao_cor;
  if (d.botao_texto_cor) vars["--fb-btn-color"] = d.botao_texto_cor;
  vars["--fb-btn-width"] = d.botao_largura === "total" ? "100%" : "auto";
  vars["--fb-btn-justify"] = JUSTIFY[d.botao_alinhamento] || JUSTIFY.esquerda;

  return vars;
}
